<template>
  <section>
    <div class="w-full w-2/4 px-2 mb-2 md:mb-0 tw-col-span-1">
      <cl-form-group>
        <cl-form-label :label-for="t('licenses-purchased-legend')">
          {{ t("licenses-purchased-legend") }}
        </cl-form-label>
        <cl-form-input
          :id="'seats_purchased'"
          v-model:value="v$.licensesByGroup.seats_purchased.$model"
          :name="'seats_purchased'"
          :type="'number'"
          :placeholder="t('licenses-purchased-placeholder')"
          :data-test-id="`create-customer-licence-by-group`"
          :autofocus="isCurrentStep"
          :state="
            !v$.licensesByGroup.seats_purchased.$dirty
              ? undefined
              : !v$.licensesByGroup.seats_purchased.$error
          "
          :min="0"
          :max="100000"
          @on-keydown="guardNumberKeydown"
          @on-paste="guardNumberPaste"
        />
        <span
          v-if="v$.licensesByGroup.seats_purchased.$error"
          class="tw-text-system-error-500 tw-text-sm"
        >
          <span
            v-if="
              !v$.licensesByGroup.seats_purchased.maxValue.$response ||
              !v$.licensesByGroup.seats_purchased.minValue.$response ||
              !v$.licensesByGroup.seats_purchased.integer.$response ||
              !v$.licensesByGroup.seats_purchased.required.$response
            "
          >
            {{ t("licenses-purchased-validation") }}
          </span>
        </span>
      </cl-form-group>
    </div>
    <p class="tw-mb-4">{{ t("select-solutions") }}</p>
    <section
      :class="[
        hasServiceAvailable(PHISH_SERVICE)
          ? ''
          : 'tw-pointer-events-none tw-opacity-30',
      ]"
    >
      <div class="tw-flex tw-flex-row tw-justify-start">
        <cl-toggle
          id="emailSecurity.switcher"
          name="emailSecurity-switcher"
          class="tw-mr-3 tw-mb-2"
          :data-test-id="`create-customer-email-security-toggle`"
          :value="v$.emailSecurity.switcher"
          @update:value="
            (selected: boolean) => (form.emailSecurity.switcher = selected)
          "
        >
        </cl-toggle>
        <span> {{ t("email-security-solution") }} </span>
      </div>
    </section>
    <section
      :class="[
        hasServiceAvailable(PHISHING_SIMULATION)
          ? ''
          : 'tw-pointer-events-none tw-opacity-30',
      ]"
      class="tw-mb-5 tw-mt-3"
    >
      <div class="tw-flex tw-flex-row tw-justify-start">
        <cl-toggle
          id="phishingSimulation-switcher"
          name="phishingSimulation-switcher"
          class="tw-mr-3"
          :data-test-id="`create-customer-phishing-simulation-toggle`"
          :value="form.phishingSimulation.switcher"
          @update:value="
            (selected: boolean) => (form.phishingSimulation.switcher = selected)
          "
        />
        <span>
          {{ t("phishing-simulation-label") }}
        </span>
      </div>
    </section>
    <!-- security awareness training -->
    <section
      :class="[
        hasServiceAvailable(SAT_SERVICE)
          ? ''
          : 'tw-pointer-events-none tw-opacity-30',
      ]"
      class="tw-mb-5 tw-mt-3"
    >
      <div class="tw-flex tw-flex-row tw-justify-start">
        <cl-toggle
          id="sat-switcher"
          name="sat-switcher"
          class="tw-mr-3"
          :data-test-id="`create-customer-sat-toggle`"
          :value="form.sat.switcher"
          @update:value="(selected: boolean) => (form.sat.switcher = selected)"
        />
        <span>
          {{ t("sat-label") }}
        </span>
      </div>
    </section>
    <section
      :class="[
        hasServiceAvailable(ARC_SERVICE) && isCountryAvailable
          ? ''
          : 'tw-pointer-events-none tw-opacity-30',
      ]"
      class="tw-mt-3 tw-mb-8"
    >
      <div class="tw-mb-3 tw-flex tw-flex-row tw-justify-start">
        <cl-toggle
          id="emailArchiving-switcher"
          name="emailArchiving-switcher"
          class="tw-mr-3"
          :data-test-id="`create-customer-email-archiving-toggle`"
          :value="form.emailArchiving.switcher"
          @update:value="
            (selected: boolean) => (form.emailArchiving.switcher = selected)
          "
        />
        <span>
          {{ t("email-archiving-label") }}
        </span>
      </div>
    </section>
  </section>
</template>
<script lang="ts">
type Form = {
  licensesByGroup: {
    seats_purchased: string;
  };
  emailSecurity: {
    readonly seats_purchased: string;
    switcher: boolean;
  };
  emailArchiving: {
    readonly seats_purchased: string;
    switcher: boolean;
  };
  phishingSimulation: {
    readonly seats_purchased: string;
    switcher: boolean;
  };
  sat: {
    readonly seats_purchased: string;
    switcher: boolean;
  };
};

interface Props {
  form: Form;
  isCurrentStep: boolean;
  isCountryAvailable: boolean;
  allLicences: ResellerOrganisationResponse;
}
</script>
<script setup lang="ts">
import { reactive, watch, watchEffect } from "vue";
import {
  LicenceWithSeats,
  ResellerOrganisationResponse,
} from "@/types/licenceSolutions.ts";
import { useVuelidate } from "@vuelidate/core";
import {
  integer,
  maxValue,
  minValue,
  numeric,
  required,
} from "@vuelidate/validators";
import {
  ARC_SERVICE,
  PHISH_SERVICE,
  PHISHING_SIMULATION,
  SAT_SERVICE,
} from "@/utils/services";
import { useI18n } from "vue-i18n";
import { guardNumberKeydown, guardNumberPaste } from "@/utils/inputGuards";

const props = defineProps<Props>();
const form = reactive<Form>({ ...props.form });
const { t } = useI18n();

const emit = defineEmits<{
  (e: "update:form", form: Form): void;
  (e: "formValidation", isValid: boolean): void;
}>();

const rules = {
  licensesByGroup: {
    seats_purchased: {
      required,
      numeric,
      integer,
      minValue: minValue(1),
      maxValue: maxValue(100000),
    },
  },
  emailSecurity: {
    seats_purchased: { required, numeric, integer, maxValue: maxValue(100000) },
  },
  emailArchiving: {
    seats_purchased: { required, numeric, integer, maxValue: maxValue(100000) },
  },
  phishingSimulation: {
    seats_purchased: { required, numeric, integer, maxValue: maxValue(100000) },
  },
  sat: {
    seats_purchased: { required, numeric, integer, maxValue: maxValue(100000) },
  },
};
const v$ = useVuelidate(rules, form);

const hasServiceAvailable = (service_uuid: string) => {
  return (
    props.allLicences.licences?.some(
      (availableLicence: LicenceWithSeats) =>
        availableLicence.solution_uuid === service_uuid,
    ) ?? false
  );
};

watch(form, (newValue) => {
  emit("update:form", newValue);
});

watchEffect(() => {
  emit("formValidation", v$.value.$invalid);
});
</script>
